<template>
  <div ref="renderArea" class="background-render-area relative w-full h-full grid"
  :style="{
    gridTemplateColumns: `${sectionDimensions.cols[0]}px ${sectionDimensions.cols[1]}px`,
    gridTemplateRows: `${sectionDimensions.rows[0]}px ${sectionDimensions.rows[1]}px ${sectionDimensions.rows[2]}px`,
    '--dot-grid-spacing': `${dotGridSpacing}px`,
    '--mask-cutoff-left': `${maskCutoff.left}px`,
    '--mask-cutoff-right': `${maskCutoff.right}px`,
    '--mask-cutoff-top': `${maskCutoff.top}px`,
    '--mask-cutoff-bottom': `${maskCutoff.bottom}px`
  }">
    <div class="dot-grid top-left" 
    :class="{
      'scaling-active': performanceDirection === 'positive' && hoveredCategory === 'scaling',
      'winners-active': performanceDirection === 'negative' && hoveredCategory === 'winners',
      'all-active': hoveredCategory === 'all'
    }"/>
    <div ref="topRightDotGrid" class="dot-grid top-right" 
    :class="{ 
      'winners-active': performanceDirection === 'positive' && hoveredCategory === 'winners',
      'scaling-active': performanceDirection === 'negative' && hoveredCategory === 'scaling',
      'all-active': hoveredCategory === 'all'
    }"/>
    <div ref="spaceConstrainer" class="dot-grid left" />
    <div class="dot-grid right" />
    <div ref="bottomLeftDotGrid" class="dot-grid left" 
    :class="{
      'losers-active': hoveredCategory === 'losers',
      'all-active': hoveredCategory === 'all'
    }" 
    />
    <div class="dot-grid right" 
    :class="{
      'losers-active': hoveredCategory === 'losers',
      'all-active': hoveredCategory === 'all'
    }" 
    />
    <!-- Dividing Lines -->
    <svg class="absolute top-0 left-0 w-full h-full" :viewBox="`0 0 ${sectionDimensions.areaWidth} ${sectionDimensions.areaHeight}`">
      <line v-for="(line, index) in lineDimensions" :key="`scatter-line-${index}`"
      :x1="line.x1" :y1="line.y1" :x2="line.x2" :y2="line.y2"
      stroke="#EBEFF3" stroke-width="1.5" stroke-linecap="round"/>
    </svg>
  </div>
</template>

<script>
const IDEAL_DOT_SPACING = 8

export default {
  name: 'BackgroundRenderer',
  props: {
    axisScales: {
      type: Array,
      default: () => []
    },
    performanceThreshold: {
      type: Number,
      default: 0
    },
    performanceDirection: {
      type: String,
      default: 'positive'
    },
    scaleThreshold: {
      type: Number,
      default: 0
    },
    losingThreshold: {
      type: Number,
      default: 0
    },
    dataAreaPadding: {
      type: Object,
      default: () => ({ top: 0, left: 0, right: 0 })
    },
    hoveredCategory: {
      type: String
    }
  },
  data () {
    return {
      sectionDimensions: { cols: [0, 0], rows: [0, 0, 0], areaWidth: 0, areaHeight: 0 },
      dotGridSpacing: IDEAL_DOT_SPACING,
      maskCutoff: { left: 0, right: 0, top: 0, bottom: 0 }
    }
  },
  watch: {
    axisScales: {
      handler () {
        this.computeSectionGridDimensions()
      }
    }
  },
  computed: {
    lineDimensions () {
      const line_1 = {
        x1: this.sectionDimensions.cols[0],
        y1: 0,
        x2: this.sectionDimensions.cols[0],
        y2: this.sectionDimensions.areaHeight
      }
      const line_2 = {
        x1: 0,
        y1: this.sectionDimensions.areaHeight - this.sectionDimensions.rows[2],
        x2: this.sectionDimensions.areaWidth,
        y2: this.sectionDimensions.areaHeight - this.sectionDimensions.rows[2]
      }
      const line_3 = {
        x1: 0,
        y1: this.sectionDimensions.rows[0],
        x2: this.sectionDimensions.areaWidth,
        y2: this.sectionDimensions.rows[0]
      }
      return [line_1, line_2, line_3]
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.computeSectionGridDimensions()
    })
  },
  methods: {
    computeSectionGridDimensions () {
      const [ xScale, yScale ] = this.axisScales
      if (!xScale || !yScale || !this.$refs.renderArea) return

      const renderArea = this.$refs.renderArea
      const renderAreaWidth = renderArea.clientWidth
      const renderAreaHeight = renderArea.clientHeight
      const { top: topPadding, bottom: bottomPadding, left: leftPadding, right: rightPadding } = this.dataAreaPadding

      const dimensions = { cols: [], rows: [] }

      // Compute the column widths
      xScale.range([leftPadding, renderAreaWidth - rightPadding])
      const leftColWidth = xScale(this.performanceThreshold)
      dimensions.cols.push(leftColWidth)
      dimensions.cols.push(renderAreaWidth - leftColWidth)
      
      // Compute the row heights
      yScale.range([bottomPadding, renderAreaHeight - topPadding])
      const bottomRowHeight = yScale(this.losingThreshold)
      const middleRowHeight = yScale(this.scaleThreshold) - bottomRowHeight
      const topRowHeight = renderAreaHeight - middleRowHeight - bottomRowHeight
      dimensions.rows.push(topRowHeight)
      dimensions.rows.push(middleRowHeight)
      dimensions.rows.push(bottomRowHeight)

      this.sectionDimensions = { ...dimensions, areaWidth: renderAreaWidth, areaHeight: renderAreaHeight }
      this.$nextTick(() => {
        this.computeDotGridSpacing()
      })
    },
    computeDotGridSpacing () {
      const spaceConstrainer = this.$refs.spaceConstrainer
      if (!spaceConstrainer) return

      const spaceConstrainerHeight = spaceConstrainer.getBoundingClientRect().height + 2 // +2px for circle diameter offset
      const numDotRows = Math.round(spaceConstrainerHeight / IDEAL_DOT_SPACING)
      const dotGridSpacing = spaceConstrainerHeight > IDEAL_DOT_SPACING 
        ? spaceConstrainerHeight / numDotRows 
        : IDEAL_DOT_SPACING // If the space constrainer is too small (e.g. scaling threshold = losing threshold), use the ideal dot spacing
      this.dotGridSpacing = dotGridSpacing

      // Compute the mask cutoff values (making cleaner boundaries at the edges of the render area)
      const topRightDotGrid = this.$refs.topRightDotGrid
      const bottomLeftDotGrid = this.$refs.bottomLeftDotGrid
      if (!topRightDotGrid || !bottomLeftDotGrid) return

      const topRightDotGridHeight = topRightDotGrid.getBoundingClientRect().height
      const topRightDotGridWidth = topRightDotGrid.getBoundingClientRect().width
      const bottomLeftDotGridHeight = bottomLeftDotGrid.getBoundingClientRect().height
      const bottomLeftDotGridWidth = bottomLeftDotGrid.getBoundingClientRect().width

      const renderedRightDotWidth = Math.floor(topRightDotGridWidth / dotGridSpacing) * dotGridSpacing
      const renderedLeftDotWidth = Math.floor(bottomLeftDotGridWidth / dotGridSpacing) * dotGridSpacing
      const renderedTopDotHeight = Math.floor(topRightDotGridHeight / dotGridSpacing) * dotGridSpacing
      const renderedBottomDotHeight = Math.floor(bottomLeftDotGridHeight / dotGridSpacing) * dotGridSpacing

      this.maskCutoff = {
        left: bottomLeftDotGridWidth - renderedLeftDotWidth,
        right: topRightDotGridWidth - renderedRightDotWidth,
        top: topRightDotGridHeight - renderedTopDotHeight,
        bottom: bottomLeftDotGridHeight - renderedBottomDotHeight
      }
    }
  }
}
</script>

<style scoped>
.background-render-area {
  clip-path: inset(var(--mask-cutoff-top) var(--mask-cutoff-right) var(--mask-cutoff-bottom) var(--mask-cutoff-left));
}

@property --dot-color {
  syntax: '<color>';
  inherits: false;
  initial-value: rgba(235, 239, 243, 0.75);
}
.dot-grid {
  background-image: radial-gradient(circle at 1px 1px, var(--dot-color) 1px, transparent 0);
  background-size: var(--dot-grid-spacing) var(--dot-grid-spacing);
  transition: --dot-color 200ms ease-in-out;

  /* Uncomment to see dot grid cell boundaries (for debugging) */
  /* background-image: 
    linear-gradient(rgba(255, 0, 0, 0.2) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 0, 0, 0.2) 1px, transparent 1px),
    radial-gradient(circle at 1px 1px, var(--dot-color) 1px, transparent 0);
  background-size: 
    var(--dot-grid-spacing) var(--dot-grid-spacing),
    var(--dot-grid-spacing) var(--dot-grid-spacing),
    var(--dot-grid-spacing) var(--dot-grid-spacing); */
}
.dot-grid.left {
  background-position: 100% -2px;
}
.dot-grid.right {
  background-position: -2px -2px;
}
.dot-grid.top-left {
  background-position: 100% 100%;
}
.dot-grid.top-right {
  background-position: -2px 100%;
}
.dot-grid.winners-active {
  --dot-color: rgba(64, 196, 170, 1)
}
.dot-grid.scaling-active {
  --dot-color: rgba(198, 226, 255, 1)
}
.dot-grid.losers-active {
  --dot-color: rgba(252, 216, 223, 1)
}
.dot-grid.all-active {
  --dot-color: rgba(223, 225, 230, 1)
}
</style>